import { ApiClient } from '../config';
import type { Customer } from '../../types/domain';
import type { CreateCustomerInput, UpdateCustomerInput } from '../../types/api';

export interface CustomerFilters {
  status?: string;
  includeDeleted?: boolean;
}

export interface CustomerService {
  getCustomers(filters?: CustomerFilters): Promise<Customer[]>;
  getDeletedCustomers(): Promise<Customer[]>;
  getCustomerById(id: string, includeDeleted?: boolean): Promise<Customer>;
  createCustomer(data: CreateCustomerInput): Promise<Customer>;
  updateCustomer(id: string, data: UpdateCustomerInput): Promise<Customer>;
  deleteCustomer(id: string): Promise<void>;
  restoreCustomer(id: string): Promise<void>;
  hardDeleteCustomer(id: string): Promise<void>;
  checkCustomerDeletable(id: string): Promise<{ canDelete: boolean; message?: string; activeActivations?: number; activeOrNonRevokedLicenses?: number; }>;
}

export class CustomerServiceImpl implements CustomerService {
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  private parseModuleIds(moduleIds: any): string[] {
    if (!moduleIds) return [];
    if (Array.isArray(moduleIds)) {
      // Handle doubly stringified arrays: ["[\"mod_epilepsy\",\"mod_oncology\"]"]
      if (moduleIds.length === 1 && typeof moduleIds[0] === 'string' && moduleIds[0].startsWith('[')) {
        try {
          return JSON.parse(moduleIds[0]);
        } catch (e) {
          console.error('Failed to parse moduleIds:', e);
          return [];
        }
      }
      return moduleIds;
    }
    if (typeof moduleIds === 'string') {
      try {
        return JSON.parse(moduleIds);
      } catch (e) {
        console.error('Failed to parse moduleIds string:', e);
        return [];
      }
    }
    return [];
  }

  private transformCustomer(customer: any): Customer {
    console.log('[CustomerService] Transforming customer:', customer);
    try {
      const transformedCustomer = {
        ...customer,
        // Provide default values for required fields that might be missing
        type: customer.type || 'organization',
        inheritLicenses: customer.inheritLicenses ?? false,
        deletedAt: customer.deletedAt || null,
        // Transform licenses if they exist
        licenses: customer.licenses?.map((license: any) => ({
          ...license,
          moduleIds: this.parseModuleIds(license.moduleIds)
        })) || []
      };
      console.log('[CustomerService] Transformed customer:', transformedCustomer);
      return transformedCustomer;
    } catch (error) {
      console.error('[CustomerService] Error transforming customer:', error);
      throw error;
    }
  }

  async getCustomers(filters?: CustomerFilters): Promise<Customer[]> {
    try {
      console.log('[CustomerService] Fetching customers with filters:', filters);
      const queryParams = new URLSearchParams();
      if (filters?.status) {
        queryParams.append('status', filters.status);
      }
      if (filters?.includeDeleted !== undefined) {
        queryParams.append('includeDeleted', String(filters.includeDeleted));
      }

      const path = `/api/v1/customers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await this.apiClient.get<any[]>(path);
      console.log('[CustomerService] Raw response data:', data);

      const transformedCustomers = data.map(customer => this.transformCustomer(customer));
      console.log('[CustomerService] Transformed customers:', transformedCustomers);
      return transformedCustomers;
    } catch (error) {
      console.error('[CustomerService] Error fetching customers:', error);
      throw error;
    }
  }

  async getDeletedCustomers(): Promise<Customer[]> {
    try {
      console.log('[CustomerService] Fetching deleted customers');
      const data = await this.apiClient.get<any[]>('/api/v1/customers/trash');
      console.log('[CustomerService] Raw response data:', data);

      const transformedCustomers = data.map(customer => this.transformCustomer(customer));
      console.log('[CustomerService] Transformed customers:', transformedCustomers);
      return transformedCustomers;
    } catch (error) {
      console.error('[CustomerService] Error fetching deleted customers:', error);
      throw error;
    }
  }

  async getCustomerById(id: string, includeDeleted: boolean = false): Promise<Customer> {
    if (!id) throw new Error('Customer ID is required');

    try {
      console.log('[CustomerService] Fetching customer by ID:', id);
      const queryParams = new URLSearchParams();
      queryParams.append('includeDeleted', String(includeDeleted));

      const path = `/api/v1/customers/${id}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await this.apiClient.get<any>(path);
      const transformedCustomer = this.transformCustomer(data);
      return transformedCustomer;
    } catch (error) {
      console.error('[CustomerService] Error fetching customer:', error);
      throw error;
    }
  }

  async createCustomer(data: CreateCustomerInput): Promise<Customer> {
    try {
      console.log('[CustomerService] Creating customer:', data);
      const response = await this.apiClient.post<any>('/api/v1/customers', data);
      return this.transformCustomer(response);
    } catch (error) {
      console.error('[CustomerService] Error creating customer:', error);
      throw error;
    }
  }

  async updateCustomer(id: string, data: UpdateCustomerInput): Promise<Customer> {
    if (!id) throw new Error('Customer ID is required');

    try {
      console.log('[CustomerService] Updating customer:', { id, data });
      const response = await this.apiClient.put<any>(`/api/v1/customers/${id}`, data);
      return this.transformCustomer(response);
    } catch (error) {
      console.error('[CustomerService] Error updating customer:', error);
      throw error;
    }
  }

  async deleteCustomer(id: string): Promise<void> {
    if (!id) throw new Error('Customer ID is required');

    try {
      // Instead of DELETE, we'll use PATCH to set the deletedAt timestamp
      await this.apiClient.patch(`/api/v1/customers/${id}/soft-delete`, {});
    } catch (error) {
      console.error('[CustomerService] Error deleting customer:', error);
      throw error;
    }
  }

  async restoreCustomer(id: string): Promise<void> {
    if (!id) throw new Error('Customer ID is required');

    try {
      await this.apiClient.patch(`/api/v1/customers/${id}/restore`, {});
    } catch (error) {
      console.error('[CustomerService] Error restoring customer:', error);
      throw error;
    }
  }

  async hardDeleteCustomer(id: string): Promise<void> {
    if (!id) throw new Error('Customer ID is required');

    try {
      await this.apiClient.delete(`/api/v1/customers/${id}`);
    } catch (error) {
      console.error('[CustomerService] Error hard deleting customer:', error);
      throw error;
    }
  }

  async checkCustomerDeletable(id: string): Promise<{ canDelete: boolean; message?: string; activeActivations?: number; activeOrNonRevokedLicenses?: number; }> {
    if (!id) throw new Error('Customer ID is required');

    try {
      const response = await this.apiClient.get<{ canDelete: boolean; message?: string; activeActivations?: number; activeOrNonRevokedLicenses?: number; }>(`/api/v1/customers/${id}/check-deletable`);
      return response;
    } catch (error) {
      console.error('[CustomerService] Error checking if customer is deletable:', error);
      throw error;
    }
  }
}

export const customerService = new CustomerServiceImpl(new ApiClient());

export function createCustomerService(apiClient: ApiClient): CustomerService {
  return new CustomerServiceImpl(apiClient);
}
